import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { useResponsive } from '../hooks/useIsMobile';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, CheckCircle } from '../components/Icons';
import logo from '../assets/logo.png';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

function Register() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const isID = i18n.language === 'id';

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError(isID ? 'Password minimal 6 karakter.' : 'Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: name } }
    });
    if (error) {
      setError(error.message);
    } else {
      setRegistered(true);
    }
    setLoading(false);
  };

  const handleGoogleRegister = async () => {
    setGoogleLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (error) {
      setError(isID ? 'Gagal mendaftar dengan Google.' : 'Failed to sign up with Google.');
      setGoogleLoading(false);
    }
  };

  if (registered) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>
              <CheckCircle size={56} color="var(--teal-primary)" strokeWidth={1.5} />
            </div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--ink)', marginBottom: '10px' }}>
              {isID ? 'Akun berhasil dibuat!' : 'Account created!'}
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--body)', lineHeight: '1.6', marginBottom: '24px' }}>
              {isID
                ? `Email konfirmasi telah dikirim ke ${email}. Silakan cek inbox Anda untuk mengaktifkan akun.`
                : `A confirmation email has been sent to ${email}. Please check your inbox to activate your account.`}
            </p>
            <button
              className="btn-primary-animated"
              style={styles.btnPrimary}
              onClick={() => navigate('/login')}>
              {isID ? 'Lanjut ke Login' : 'Go to Login'}
              <ArrowRight size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, maxWidth: isMobile ? '100%' : '440px' }}>

        {/* Logo */}
        <div style={styles.logoRow} onClick={() => navigate('/landing')}>
          <img src={logo} alt="NOWGUT" style={styles.logoImg} />
          <div>
            <div style={styles.logoName}>NOWGUT</div>
            <div style={styles.logoTagline}>
              {isID ? 'Pemantau Risiko Saluran Cerna Anak' : 'GI Risk Monitor for Children with ASD'}
            </div>
          </div>
        </div>

        <div style={styles.welcomeBox}>
          <h1 style={styles.title}>
            {isID ? 'Buat akun gratis' : 'Create free account'}
          </h1>
          <p style={styles.subtitle}>
            {isID
              ? 'Bergabung dengan NOWGUT dan mulai pantau kesehatan pencernaan anak Anda.'
              : 'Join NOWGUT and start monitoring your child\'s digestive health.'}
          </p>
        </div>

        {error && <div style={styles.errorBox}>⚠️ {error}</div>}

        {/* Google Button */}
        <button
          className="btn-secondary-animated"
          style={styles.googleBtn}
          onClick={handleGoogleRegister}
          disabled={googleLoading}>
          {googleLoading ? (
            <span>{isID ? 'Menghubungkan...' : 'Connecting...'}</span>
          ) : (
            <>
              <GoogleIcon />
              {isID ? 'Daftar dengan Google' : 'Continue with Google'}
            </>
          )}
        </button>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>
            {isID ? 'atau daftar dengan email' : 'or sign up with email'}
          </span>
          <div style={styles.dividerLine} />
        </div>

        {/* Form */}
        <form onSubmit={handleRegister}>
          {/* Nama */}
          <div style={styles.field}>
            <label style={styles.label}>{t('auth.name')}</label>
            <div style={styles.inputWrapper}>
              <User size={16} style={styles.inputIcon} color="var(--body-mid)" />
              <input
                style={styles.inputWithIcon}
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={isID ? 'Nama lengkap Anda' : 'Your full name'}
                required />
            </div>
          </div>

          {/* Email */}
          <div style={styles.field}>
            <label style={styles.label}>{t('auth.email')}</label>
            <div style={styles.inputWrapper}>
              <Mail size={16} style={styles.inputIcon} color="var(--body-mid)" />
              <input
                style={styles.inputWithIcon}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="email@contoh.com"
                required />
            </div>
          </div>

          {/* Password */}
          <div style={styles.field}>
            <label style={styles.label}>{t('auth.password')}</label>
            <div style={styles.inputWrapper}>
              <Lock size={16} style={styles.inputIcon} color="var(--body-mid)" />
              <input
                style={styles.inputWithIcon}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={isID ? 'Min. 6 karakter' : 'Min. 6 characters'}
                required />
              <button
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowPassword(!showPassword)}>
                {showPassword
                  ? <EyeOff size={16} color="var(--body-mid)" />
                  : <Eye size={16} color="var(--body-mid)" />}
              </button>
            </div>

            {/* Password strength hint */}
            {password.length > 0 && (
              <div style={{ marginTop: '6px', display: 'flex', gap: '4px' }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{
                    flex: 1, height: '3px',
                    borderRadius: '2px',
                    backgroundColor:
                      password.length < 6 ? (i === 1 ? '#DC2626' : 'var(--neutral-gray)') :
                      password.length < 10 ? (i <= 2 ? '#D97706' : 'var(--neutral-gray)') :
                      'var(--success)',
                    transition: 'background-color 0.2s',
                  }} />
                ))}
              </div>
            )}
            {password.length > 0 && password.length < 6 && (
              <p style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>
                {isID ? 'Password terlalu pendek' : 'Password too short'}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="btn-primary-animated"
            style={styles.btnPrimary}
            disabled={loading}>
            {loading
              ? (isID ? 'Membuat akun...' : 'Creating account...')
              : (isID ? 'Buat Akun' : 'Create Account')}
            {!loading && <ArrowRight size={16} strokeWidth={2} />}
          </button>
        </form>

        {/* Terms */}
        <p style={{ fontSize: '11px', color: 'var(--body-mid)', textAlign: 'center', marginTop: '12px', lineHeight: '1.5' }}>
          {isID
            ? 'Dengan mendaftar, Anda menyetujui penggunaan data untuk tujuan pemantauan kesehatan anak.'
            : 'By signing up, you agree to the use of data for child health monitoring purposes.'}
        </p>

        <p style={styles.switchText}>
          {isID ? 'Sudah punya akun?' : 'Already have an account?'}{' '}
          <Link to="/login" style={styles.switchLink}>
            {isID ? 'Masuk' : 'Sign in'}
          </Link>
        </p>

        <p style={styles.backLink}>
          <Link to="/landing" style={styles.backLinkText}>
            {isID ? '← Kembali ke beranda' : '← Back to home'}
          </Link>
        </p>

        <div style={styles.langRow}>
          <button style={styles.langBtn}
            onClick={() => i18n.changeLanguage(isID ? 'en' : 'id')}>
            {isID ? '🇬🇧 Switch to English' : '🇮🇩 Ganti ke Bahasa Indonesia'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'var(--canvas-soft)',
    backgroundImage: ` linear-gradient( rgba(255,255,255,0.82), rgba(255,255,255,0.82) ), url('https://images.unsplash.com/photo-1489710437720-ebb67ec84dd2?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D') `, 
    backgroundSize: 'cover', 
    backgroundPosition: 'center', 
    backgroundRepeat: 'no-repeat',
    padding: '24px 16px',
  },
  card: {
    backgroundColor: 'var(--canvas)',
    padding: '40px',
    borderRadius: 'var(--radius-md)',
    width: '100%',
    border: '1px solid var(--teal-light)',
    boxShadow: 'var(--shadow-lg)',
  },
  logoRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '28px', cursor: 'pointer',
  },
  logoImg: { height: '36px', width: 'auto', objectFit: 'contain' },
  logoName: { fontSize: '15px', fontWeight: '800', color: 'var(--teal-primary)', letterSpacing: '0.05em' },
  logoTagline: { fontSize: '10px', color: 'var(--body-mid)' },
  welcomeBox: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: 'var(--ink)', marginBottom: '8px', letterSpacing: '-0.01em' },
  subtitle: { fontSize: '14px', color: 'var(--body)', lineHeight: '1.6' },
  errorBox: {
    backgroundColor: '#FEF2F2', color: '#DC2626',
    padding: '12px 14px', borderRadius: 'var(--radius-sm)',
    marginBottom: '16px', fontSize: '13px',
    border: '1px solid #FECACA',
  },
  googleBtn: {
    width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
    backgroundColor: 'var(--canvas)',
    color: 'var(--ink)',
    border: '1.5px solid var(--teal-light)',
    padding: '13px 16px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '15px', fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '20px',
    boxShadow: 'var(--shadow-sm)',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: '12px',
    marginBottom: '20px',
  },
  dividerLine: { flex: 1, height: '1px', backgroundColor: 'var(--teal-light)' },
  dividerText: { fontSize: '12px', color: 'var(--body-mid)', whiteSpace: 'nowrap' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--ink)', marginBottom: '6px' },
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  inputIcon: { position: 'absolute', left: '12px', pointerEvents: 'none' },
  inputWithIcon: {
    width: '100%', padding: '12px 42px',
    borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--teal-light)',
    fontSize: '15px', boxSizing: 'border-box',
    backgroundColor: 'var(--canvas)',
    color: 'var(--ink)', fontFamily: 'inherit',
  },
  eyeBtn: {
    position: 'absolute', right: '12px',
    background: 'none', border: 'none',
    cursor: 'pointer', padding: '4px',
    display: 'flex', alignItems: 'center',
  },
  btnPrimary: {
    width: '100%', padding: '14px',
    backgroundColor: 'var(--primary)',
    color: 'var(--on-primary)',
    border: 'none', borderRadius: 'var(--radius-md)',
    fontSize: '16px', fontWeight: '700',
    cursor: 'pointer', marginTop: '4px',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: '8px',
    boxShadow: '0 4px 12px rgba(226,120,48,0.3)',
  },
  switchText: { textAlign: 'center', marginTop: '20px', fontSize: '14px', color: 'var(--body)' },
  switchLink: { color: 'var(--teal-primary)', fontWeight: '700', textDecoration: 'none' },
  backLink: { textAlign: 'center', marginTop: '10px' },
  backLinkText: { fontSize: '13px', color: 'var(--body-mid)', textDecoration: 'none' },
  langRow: { marginTop: '24px', textAlign: 'center', paddingTop: '20px', borderTop: '1px solid var(--teal-light)' },
  langBtn: {
    background: 'none', border: 'none',
    color: 'var(--body-mid)', fontSize: '12px',
    cursor: 'pointer', textDecoration: 'underline',
  },
};

export default Register;